import React from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import MediaQuery from "react-responsive"
import colors from "../../Constants/colors"
import PYPLayoutContainer from "../PYPLayoutContainer/PYPLayoutContainer.js"
import PYPPartTitleContent from "../PYPPartTitleContent/PYPPartTitleContent.js"
import Actions from "../../Redux/Actions"
import TimeEventBarGraph from "../CommitBarGraph/CommitBarGraph"
import SkeletonWrapper from "../SkeletonWrapper/SkeletonWrapper"

const styles = {
  graphContainer: {
    color: colors.white,
    height: "450px",
    width: "90%",
    marginLeft: "auto",
    marginRight: "auto"
  },
  titleContent: {
    backgroundColor: "inherit",
    minHeight: "min-content"
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch)
}

// Memoize buttonValues and timeEvents to prevent unnecessary re-renders on hover
let memoizedButtonValues = null
let memoizedTimeEvents = null
let memoizedPartDataId = null
let memoizedTimeAsleep = null
let memoizedMetadata = null

const mapStateToProps = (state, props) => {
  const currentPart = state.pyp.parts.byId[state.pyp.navigation.currentPartId]
  if (!currentPart) {
    return {
      ...props,
      savingPYP: state.pyp.savingPYP,
      currentPart: null,
      partDataId: null,
      metadata: { title: null, description: null },
      initializedPart: state.pyp.initializedPart,
      fetchingPartData: state.pyp.fetchingPartData,
      initializingPYPInputs: state.pyp.initializingPYPInputs,
      buttonValues: [],
      timeEvents: [],
      timeAsleep: null
    }
  }
  
  const partDataId = currentPart.partData

  let buttonValues = []
  let timeEvents = []
  let timeAsleep = null
  let metadata = { title: null, description: null }

  const partData =
    state.pyp.partData.byId[partDataId] &&
    state.pyp.partData.byId[partDataId].data
  
  if (partData) {
    // Only recalculate if partDataId changed to prevent unnecessary re-renders
    if (memoizedPartDataId !== partDataId || !memoizedButtonValues || !memoizedTimeEvents) {
      memoizedPartDataId = partDataId
      
      const rawButtonValues = partData.referencedPartData?.buttonValues || []
      memoizedButtonValues = rawButtonValues.filter(
        (buttonValue) => {
          return buttonValue.buttonSelections && buttonValue.buttonSelections.length > 0
        }
      )
      
      memoizedTimeEvents = partData.referencedPartData?.timeEvents || []
      
      // Memoize metadata to prevent object recreation
      memoizedMetadata = partData.partTitle && partData.partTitle[0] 
        ? { ...partData.partTitle[0] } 
        : { title: null, description: null }
      
      // Calculate timeAsleep only if constraints exist
      memoizedTimeAsleep = null
      if (partData.referencedPartData?.constraints && 
          partData.referencedPartData.constraints.length >= 2 &&
          partData.referencedPartData.constraints[0]?.text &&
          partData.referencedPartData.constraints[1]?.text) {
        const wakeTime = partData.referencedPartData.constraints[0].text.split(":")
        const bedTime = partData.referencedPartData.constraints[1].text.split(":")
        
        if (wakeTime.length >= 2 && bedTime.length >= 2) {
          const wakeHours = parseInt(wakeTime[0], 10)
          const wakeMinutes = parseInt(wakeTime[1], 10)
          const bedHours = parseInt(bedTime[0], 10)
          const bedMinutes = parseInt(bedTime[1], 10)
          
          if (!isNaN(wakeHours) && !isNaN(wakeMinutes) && !isNaN(bedHours) && !isNaN(bedMinutes)) {
            memoizedTimeAsleep =
              bedHours * 60 +
              bedMinutes +
              (24 * 60) -
              (wakeHours * 60) -
              wakeMinutes
          }
        }
      }
    }
    
    buttonValues = memoizedButtonValues
    timeEvents = memoizedTimeEvents
    timeAsleep = memoizedTimeAsleep
    metadata = memoizedMetadata
  } else {
    // Reset memoization if partData is not available
    memoizedPartDataId = null
    memoizedButtonValues = null
    memoizedTimeEvents = null
    memoizedTimeAsleep = null
    memoizedMetadata = { title: null, description: null }
  }

  return {
    ...props,
    // Only include specific pyp state values instead of entire object to prevent unnecessary re-renders
    savingPYP: state.pyp.savingPYP,
    currentPart,
    partDataId,
    metadata,
    initializedPart: state.pyp.initializedPart,
    fetchingPartData: state.pyp.fetchingPartData,
    initializingPYPInputs: state.pyp.initializingPYPInputs,
    buttonValues,
    timeEvents,
    timeAsleep
  }
}

// Memoize the component to prevent re-renders when only tooltip state changes
const LayoutMetaDataGraphsComponent = class LayoutMetaDataGraphs extends React.Component {
    constructor(props) {
      super(props)
      this.state = {
        skeletonWidth: 0,
        skeletonWidthRatio: 0
      }
    }
    
    shouldComponentUpdate(nextProps, nextState) {
      // Only update if relevant props change, not on every state change
      // This prevents re-renders when hovering (which might trigger internal nivo state changes)
      // Since we're memoizing buttonValues and timeEvents, we can use reference equality
      
      // Always allow update if data is being loaded for the first time
      if ((!this.props.buttonValues || !this.props.timeEvents) && 
          (nextProps.buttonValues || nextProps.timeEvents)) {
        return true
      }
      
      // Always allow update if partDataId changes (new part loaded)
      if (nextProps.partDataId !== this.props.partDataId) {
        return true
      }
      
      const propsChanged = 
        nextProps.buttonValues !== this.props.buttonValues ||
        nextProps.timeEvents !== this.props.timeEvents ||
        nextProps.timeAsleep !== this.props.timeAsleep ||
        nextProps.metadata !== this.props.metadata ||
        nextProps.fetchingPartData !== this.props.fetchingPartData ||
        nextProps.currentPart?.id !== this.props.currentPart?.id
      
      const stateChanged = 
        nextState.skeletonWidth !== this.state.skeletonWidth ||
        nextState.skeletonWidthRatio !== this.state.skeletonWidthRatio
      
      // Never update if only savingPYP, initializedPart, or initializingPYPInputs changed
      // These are internal state flags that shouldn't affect the graph display
      if (!propsChanged && !stateChanged) {
        return false
      }
      
      return propsChanged || stateChanged
    }

    resizeSkeletonWidth = () => {
      const container = document.getElementById("skeletonContainer")
      this.setState({
        skeletonWidth: container.clientWidth * 0.8,
        skeletonWidthRatio: container.clientWidth / 400
      })
    }

    componentWillUnmount() {
      window.removeEventListener("resize", this.resizeSkeletonWidth)
      if (this.fetchTimeout) {
        clearTimeout(this.fetchTimeout)
      }
    }

    componentDidMount() {
      this.resizeSkeletonWidth()
      window.addEventListener("resize", this.resizeSkeletonWidth)
      console.log('[LayoutMetaDataGraphs] componentDidMount', {
        currentPartId: this.props.currentPart?.id,
        partDataId: this.props.partDataId,
        savingPYP: this.props.savingPYP,
        fetchingPartData: this.props.fetchingPartData
      })
      
      // Fetch data if not saving and we have a current part
      // Even if partDataId exists, we should fetch to ensure we have the latest data
      if (!this.props.savingPYP && this.props.currentPart?.id && !this.props.fetchingPartData) {
        console.log('[LayoutMetaDataGraphs] componentDidMount - fetching part data')
        this.props
          .fetchPartData()
          .then(() => {
            console.log('[LayoutMetaDataGraphs] fetchPartData completed in componentDidMount')
            this.setState({ loading: false })
          })
          .catch((error) => {
            console.error('[LayoutMetaDataGraphs] fetchPartData failed in componentDidMount:', error)
            this.setState({ loading: false })
          })
      } else {
        console.log('[LayoutMetaDataGraphs] componentDidMount - skipping fetch', {
          savingPYP: this.props.savingPYP,
          hasCurrentPart: !!this.props.currentPart?.id,
          fetchingPartData: this.props.fetchingPartData
        })
      }
    }

    componentDidUpdate(prevProps) {
      console.log('[LayoutMetaDataGraphs] componentDidUpdate', {
        prevPartId: prevProps.currentPart?.id,
        currentPartId: this.props.currentPart?.id,
        prevPartDataId: prevProps.partDataId,
        currentPartDataId: this.props.partDataId,
        prevFetchingPartData: prevProps.fetchingPartData,
        currentFetchingPartData: this.props.fetchingPartData,
        prevSavingPYP: prevProps.savingPYP,
        currentSavingPYP: this.props.savingPYP
      })
      
      // Fetch data when part changes (navigating to a new part)
      const partChanged = prevProps.currentPart?.id !== this.props.currentPart?.id
      
      // Also check if partDataId is missing (data hasn't been fetched yet)
      const partDataMissing = !this.props.partDataId && this.props.currentPart?.id
      
      if ((partChanged || partDataMissing) && this.props.currentPart?.id && !this.props.fetchingPartData && !this.props.savingPYP) {
        console.log('[LayoutMetaDataGraphs] Part changed or data missing, fetching data', {
          prevPartId: prevProps.currentPart?.id,
          currentPartId: this.props.currentPart?.id,
          partDataId: this.props.partDataId,
          partChanged,
          partDataMissing
        })
        clearTimeout(this.fetchTimeout)
        this.fetchTimeout = setTimeout(() => {
          this.props
            .fetchPartData()
            .then(() => {
              console.log('[LayoutMetaDataGraphs] fetchPartData completed after part change/data missing')
              this.setState({ loading: false })
            })
            .catch((error) => {
              console.error('[LayoutMetaDataGraphs] fetchPartData failed after part change/data missing:', error)
              this.setState({ loading: false })
            })
        }, 100)
        return
      }

      // Only fetch if savingPYP changed from true to false AND we're not already fetching
      // AND the part hasn't changed (to prevent unnecessary fetches on re-renders)
      // This prevents tooltip hovers from triggering unnecessary data fetches
      // Also check that we actually need to fetch (partDataId should exist and be valid)
      if (
        prevProps.savingPYP &&
        !this.props.savingPYP &&
        !this.props.fetchingPartData &&
        !partChanged &&
        prevProps.currentPart?.id === this.props.currentPart?.id &&
        prevProps.partDataId === this.props.partDataId &&
        this.props.partDataId && // Ensure partDataId exists
        this.props.currentPart?.id // Ensure currentPart.id exists
      ) {
        // Use a small timeout to debounce rapid updates and prevent hover-triggered fetches
        clearTimeout(this.fetchTimeout)
        this.fetchTimeout = setTimeout(() => {
          this.props
            .fetchPartData()
            .then(() => {
              this.setState({ loading: false })
            })
            .catch((error) => {
              console.error('[LayoutMetaDataGraphs] fetchPartData failed:', error)
              this.setState({ loading: false })
            })
        }, 100)
      }
    }

    render() {
      console.log('[LayoutMetaDataGraphs] render called', {
        currentPartId: this.props.currentPart?.id,
        partDataId: this.props.partDataId,
        hasButtonValues: !!(this.props.buttonValues && this.props.buttonValues.length > 0),
        buttonValuesCount: this.props.buttonValues?.length || 0,
        hasTimeEvents: !!(this.props.timeEvents && this.props.timeEvents.length > 0),
        timeEventsCount: this.props.timeEvents?.length || 0,
        timeAsleep: this.props.timeAsleep,
        fetchingPartData: this.props.fetchingPartData,
        savingPYP: this.props.savingPYP,
        hasMetadata: !!this.props.metadata
      })

      let eventTitles = ["Total"]
      let longestValue = 9
      
      // Fix: timeAwake should be a number, not an array
      // Calculate hours awake (24 hours - hours asleep)
      const timeAsleepHours = this.props.timeAsleep ? this.props.timeAsleep / 60 : 0
      const timeAwake = 24 - timeAsleepHours
      
      // Prevent division by zero
      const timeAwakeMinutes = timeAwake * 60
      const divisor = timeAwakeMinutes > 0 ? timeAwakeMinutes : 1440 // Default to 24 hours if invalid
      
      console.log('[LayoutMetaDataGraphs] Calculated values:', {
        timeAsleepHours,
        timeAwake,
        timeAwakeMinutes,
        divisor
      })
      
      // Create a Set of all valid buttonValue IDs for faster lookup (used in both loops)
      const validButtonValueIds = new Set()
      if (this.props.buttonValues && Array.isArray(this.props.buttonValues)) {
        this.props.buttonValues.forEach((value) => {
          if (value && value.id !== null && value.id !== undefined) {
            const buttonValueId = Number(value.id)
            if (!isNaN(buttonValueId)) {
              validButtonValueIds.add(buttonValueId)
            }
          }
        })
      }
      
      // Ensure tempValueData is always an array
      const tempValueData =
        (this.props.buttonValues && Array.isArray(this.props.buttonValues) && this.props.buttonValues.length > 0)
          ? this.props.buttonValues.map((value) => {
          if (value.name && value.name.length > longestValue) {
            longestValue = value.name.length
          }
          const valueObj = {
            titleSequence: [],
            title: value.name || "",
            Total: 0
          }
          
          if (this.props.timeEvents && Array.isArray(this.props.timeEvents)) {
            const buttonValueId = value.id !== null && value.id !== undefined 
              ? Number(value.id) 
              : null
            
            if (buttonValueId !== null && !isNaN(buttonValueId)) {
              this.props.timeEvents.forEach((event) => {
                if (!event.duration) return
                
                // Handle both direct value_id and nested value.id (from preloaded relationship)
                let eventValueId = null
                if (event.value_id !== null && event.value_id !== undefined) {
                  eventValueId = Number(event.value_id)
                } else if (event.value && event.value.id !== null && event.value.id !== undefined) {
                  // Fallback: check if value is preloaded as a relationship
                  eventValueId = Number(event.value.id)
                }
                
                // Only match if both IDs are valid numbers and they match
                if (eventValueId !== null && !isNaN(eventValueId) && eventValueId === buttonValueId) {
                  const hours = event.duration / divisor
                  valueObj.Total += Math.round(hours * 100) / 100 // Round to 2 decimal places
                  if (event.title) {
                    valueObj[event.title] = Math.round(hours * 100) / 100
                    valueObj.titleSequence.push(event.title)
                  }
                }
              })
            }
          }
          
          valueObj.titleSequence = ["Total"]

          return valueObj
        })
          : []
      
      console.log('[LayoutMetaDataGraphs] tempValueData created:', {
        isArray: Array.isArray(tempValueData),
        length: tempValueData.length,
        buttonValuesCount: this.props.buttonValues?.length || 0
      })
      
      if (tempValueData && Array.isArray(tempValueData) && this.props.timeEvents && Array.isArray(this.props.timeEvents)) {
        const valueObj = {
          titleSequence: [],
          title: "Non-Value",
          "Non-Value": 0
        }
        
        // Use the Set created earlier for faster lookup
        this.props.timeEvents.forEach((event) => {
          if (!event.duration) return
          
          // Handle both direct value_id and nested value.id (from preloaded relationship)
          let eventValueId = null
          if (event.value_id !== null && event.value_id !== undefined) {
            eventValueId = Number(event.value_id)
          } else if (event.value && event.value.id !== null && event.value.id !== undefined) {
            // Fallback: check if value is preloaded as a relationship
            eventValueId = Number(event.value.id)
          }
          
          let isNonValue = false
          
          // If value_id is null or undefined (after checking both locations), it's definitely a non-value
          if (eventValueId === null || isNaN(eventValueId)) {
            isNonValue = true
          } else {
            // Check if value_id matches any buttonValue with proper type coercion
            if (!validButtonValueIds.has(eventValueId)) {
              isNonValue = true
            }
          }
          
          if (isNonValue) {
            const hours = event.duration / divisor
            valueObj["Non-Value"] += Math.round(hours * 100) / 100
            if (event.title) {
              valueObj[event.title] = Math.round(hours * 100) / 100
              valueObj.titleSequence.push(event.title)
            }
          }
        })
        
        // Only add "Non-Value" if there are actual non-value events with duration > 0
        if (valueObj["Non-Value"] > 0) {
          eventTitles = ["Total", "Non-Value"]
          tempValueData.push(valueObj)
        }
      }

      // eventTitles.push("Sleep") //uncomment if we want sleep to be included in breakdown
      if (tempValueData && false)
        // uncomment if we want sleep to be included in same graph
        tempValueData.push({
          titleSequence: ["Sleep"],
          title: "Sleep",
          Sleep: this.props.timeAsleep && divisor > 0 
            ? Math.round((this.props.timeAsleep / divisor) * 100) / 100 
            : 0
        })
      // Filter to only include items with titleSequence
      // Keep all values (even with Total = 0) so they show in the graph
      // The graph will show bars with 0 height for values without matching events
      let valueData = Array.isArray(tempValueData)
        ? tempValueData.filter((item) => {
          return item.titleSequence && item.titleSequence.length > 0
        })
        : []
      
      console.log('[LayoutMetaDataGraphs] valueData after filter:', {
        isArray: Array.isArray(valueData),
        length: valueData.length
      })
      
      // Normalize percentages to sum to 100% of tracked time
      // Calculate total of all values (including Non-Value)
      const totalTrackedTime = valueData && valueData.length > 0
        ? valueData.reduce((sum, item) => sum + (item.Total || 0), 0)
        : 0
      
      // Normalize each value to percentage of total tracked time
      if (totalTrackedTime > 0 && Array.isArray(valueData) && valueData.length > 0) {
        valueData = valueData.map((item) => {
          const normalizedTotal = item.Total / totalTrackedTime
          const normalizedItem = { ...item, Total: normalizedTotal }
          
          // Also normalize individual event values if they exist
          if (item.titleSequence && item.titleSequence.length > 0) {
            item.titleSequence.forEach((eventTitle) => {
              if (eventTitle !== "Total" && item[eventTitle] !== undefined) {
                normalizedItem[eventTitle] = item[eventTitle] / totalTrackedTime
              }
            })
          }
          
          return normalizedItem
        })
      }
      
      // Sort by Total (descending) - ensure valueData is an array
      if (Array.isArray(valueData) && valueData.length > 0) {
        valueData = [...valueData].sort((a, b) =>
          a.Total > b.Total ? 1 : a.Total < b.Total ? -1 : 0
        )
      
      // Move "Non-Value" to the bottom
        valueData = [...valueData].sort((a, b) =>
          a.title === "Non-Value"
            ? b.title === "Non-Value"
              ? 0
              : 1
            : b.title === "Non-Value"
            ? -1
            : 0
        )
      } else {
        valueData = []
      }
      
      console.log('[LayoutMetaDataGraphs] Final render data:', {
        hasValueData: !!(valueData && Array.isArray(valueData) && valueData.length > 0),
        valueDataType: typeof valueData,
        isArray: Array.isArray(valueData),
        valueDataLength: Array.isArray(valueData) ? valueData.length : 0,
        valueData: Array.isArray(valueData) ? valueData.map(item => ({
          title: item.title,
          Total: item.Total,
          titleSequenceLength: item.titleSequence?.length || 0
        })) : valueData,
        eventTitles,
        longestValue,
        willRenderGraph: !!(valueData && Array.isArray(valueData) && valueData.length > 0),
        fetchingPartData: this.props.fetchingPartData,
        savingPYP: this.props.savingPYP
      })
      
      return (
        <PYPLayoutContainer
          style={{ ...this.props.style }}
          footerContent={{ background: colors.midnight }}
        >
          <PYPPartTitleContent
            title={this.props.metadata.title}
            description={this.props.metadata.description}
            style={styles.titleContent}
            theme="dark"
          />
          <div
            id="skeletonContainer"
            style={{
              ...styles.graphContainer,
              height: `${(valueData && valueData.length * 37) || 350}px`
            }}
            onMouseMove={(e) => {
              // Stop propagation to prevent parent handlers from firing on hover
              // This prevents any hover-triggered navigation or reloads
              e.stopPropagation()
            }}
            onClick={(e) => {
              // Prevent any click events from bubbling up and causing navigation
              e.stopPropagation()
            }}
          >
            {valueData && valueData.length > 0 ? (
              <>
                <MediaQuery maxWidth={599}>
                  <TimeEventBarGraph
                    rightMarginModifier={-60}
                    data={valueData}
                    keys={eventTitles}
                    inColors={{
                      Total: "#00D061",
                      Sleep: "#7551E9",
                      default: "#D8F0F8",
                      "Non-Value": "#D8F0F8"
                    }}
                    tickStep={0.5}
                    yUnits="Hours"
                    xUnits="Value"
                    longestValue={longestValue}
                    percentage={false}
                  />
                </MediaQuery>
                <MediaQuery minWidth={600}>
                  <TimeEventBarGraph
                    data={valueData}
                    keys={eventTitles}
                    inColors={{
                      Total: "#00D061",
                      Sleep: "#7551E9",
                      default: "#D8F0F8",
                      "Non-Value": "#D8F0F8"
                    }}
                    tickStep={0.5}
                    yUnits="Hours"
                    xUnits="Value"
                    longestValue={longestValue}
                    percentage={false}
                  />
                </MediaQuery>
              </>
            ) : (
              <SkeletonWrapper
                theme="dark"
                height="400"
                width={this.state.skeletonWidth}
                containerStyle={{
                  width: "100%",
                  height: "400px"
                }}
              >
                <rect
                  x={20 * this.state.skeletonWidthRatio}
                  y="65"
                  width={280 * this.state.skeletonWidthRatio}
                  height="20"
                />
                <rect
                  x={20 * this.state.skeletonWidthRatio}
                  y="95"
                  width={250 * this.state.skeletonWidthRatio}
                  height="20"
                />
                <rect
                  x={20 * this.state.skeletonWidthRatio}
                  y="125"
                  width={200 * this.state.skeletonWidthRatio}
                  height="20"
                />
                <rect
                  x={20 * this.state.skeletonWidthRatio}
                  y="155"
                  width={200 * this.state.skeletonWidthRatio}
                  height="20"
                />
                <rect
                  x={20 * this.state.skeletonWidthRatio}
                  y="185"
                  width={120 * this.state.skeletonWidthRatio}
                  height="20"
                />
              </SkeletonWrapper>
            )}
          </div>
        </PYPLayoutContainer>
      )
    }
  }

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LayoutMetaDataGraphsComponent)
